import React, { useState, useRef, useCallback, useEffect } from 'react';
import { socketService } from '../../services/socket';
import { useAuth } from '../../context/AuthContext';
import TemplateSelector from './TemplateSelector';
import TemplateComposer from './TemplateComposer';
import axios from '../../utils/axios';
import { authFetch } from '../../utils/authFetch';

interface Template {
  id: string;
  name: string;
  status: string;
  category: string;
  language: string;
  components: any[];
}

interface MessageInputProps {
  onSendMessage: (content: string, mediaUrl?: string, messageType?: string) => void;
  disabled?: boolean;
  chatId?: string;
  onTemplateSent?: () => void;
}

const MessageInput: React.FC<MessageInputProps> = ({ onSendMessage, disabled, chatId, onTemplateSent }) => {
  const { user } = useAuth();
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [quickReplies, setQuickReplies] = useState<any[]>([]);
  const [showQuickReplies, setShowQuickReplies] = useState(false);
  const [filteredQuickReplies, setFilteredQuickReplies] = useState<any[]>([]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    loadQuickReplies();
  }, []);

  const loadQuickReplies = async () => {
    try {
      // axios already has /api as baseURL and auto-adds auth header
      const response = await axios.get('/quick-replies', {
        params: {
          userId: user?.id
        }
      });
      setQuickReplies(response.data);
    } catch (error) {
      console.error('Error loading quick replies:', error);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const uploadFile = async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);

    // authFetch automatically prepends /api
    const response = await authFetch('/upload', {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      throw new Error('Failed to upload file');
    }

    const data = await response.json();
    return data.data;
  };

  const getMessageType = (mimeType: string): string => {
    if (mimeType.startsWith('image/')) return 'IMAGE';
    if (mimeType.startsWith('video/')) return 'VIDEO';
    if (mimeType.startsWith('audio/')) return 'AUDIO';
    return 'DOCUMENT';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if ((!message.trim() && !selectedFile) || sending || disabled) return;
    
    // Stop typing indicator when sending
    if (chatId) {
      socketService.emit('typing_stop', chatId);
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    }
    
    setSending(true);
    setUploading(true);
    
    try {
      let mediaUrl = '';
      let messageType = 'TEXT';

      // Upload file if selected
      if (selectedFile) {
        const uploadResult = await uploadFile(selectedFile);
        mediaUrl = uploadResult.url;
        messageType = getMessageType(selectedFile.type);
      }

      await onSendMessage(message.trim(), mediaUrl, messageType);
      setMessage('');
      setSelectedFile(null);
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      alert('Failed to send message. Please try again.');
    } finally {
      setSending(false);
      setUploading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setMessage(value);
    
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }

    // Check for quick reply trigger
    const words = value.split(/\s/);
    const lastWord = words[words.length - 1];
    
    if (lastWord.startsWith('/')) {
      const searchTerm = lastWord.toLowerCase();
      const filtered = quickReplies.filter(qr => 
        qr.shortcut.toLowerCase().startsWith(searchTerm)
      );
      setFilteredQuickReplies(filtered);
      setShowQuickReplies(filtered.length > 0);
    } else {
      setShowQuickReplies(false);
    }

    // Emit typing indicator
    if (chatId && e.target.value.trim()) {
      socketService.emit('typing_start', chatId);
      
      // Clear previous timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      // Stop typing after 2 seconds of inactivity
      typingTimeoutRef.current = setTimeout(() => {
        socketService.emit('typing_stop', chatId);
      }, 2000);
    } else if (chatId && !e.target.value.trim()) {
      socketService.emit('typing_stop', chatId);
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    }
  };

  const handleQuickReplySelect = (quickReply: any) => {
    const words = message.split(/\s/);
    words[words.length - 1] = quickReply.content;
    setMessage(words.join(' '));
    setShowQuickReplies(false);
    textareaRef.current?.focus();
    
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  };

  return (
    <>
      <div className="message-input-container" style={{ position: 'relative' }}>
        {/* Quick Replies Dropdown */}
        {showQuickReplies && filteredQuickReplies.length > 0 && (
          <div style={{
            position: 'absolute',
            bottom: '100%',
            left: 0,
            right: 0,
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            boxShadow: '0 -4px 6px -1px rgba(0, 0, 0, 0.1)',
            maxHeight: '200px',
            overflowY: 'auto',
            marginBottom: '4px',
            zIndex: 10
          }}>
            {filteredQuickReplies.map((qr) => (
              <div
                key={qr.id}
                onClick={() => handleQuickReplySelect(qr)}
                style={{
                  padding: '12px',
                  cursor: 'pointer',
                  borderBottom: '1px solid #f3f4f6',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  <code style={{
                    backgroundColor: '#eff6ff',
                    color: '#1e40af',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: '500'
                  }}>
                    {qr.shortcut}
                  </code>
                  {qr.isGlobal && (
                    <span style={{
                      backgroundColor: '#dbeafe',
                      color: '#1e40af',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      fontSize: '10px',
                      fontWeight: '500'
                    }}>
                      Global
                    </span>
                  )}
                </div>
                <div style={{
                  fontSize: '13px',
                  color: '#6b7280',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  {qr.content}
                </div>
              </div>
            ))}
          </div>
        )}
        {selectedFile && (
          <div style={{
            padding: '8px 12px',
            backgroundColor: '#f3f4f6',
            borderRadius: '8px 8px 0 0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid #e5e7eb'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>📎</span>
              <span style={{ fontSize: '14px' }}>{selectedFile.name}</span>
              <span style={{ fontSize: '12px', color: '#6b7280' }}>
                ({(selectedFile.size / 1024).toFixed(1)} KB)
              </span>
            </div>
            <button
              type="button"
              onClick={() => {
                setSelectedFile(null);
                if (fileInputRef.current) {
                  fileInputRef.current.value = '';
                }
              }}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '18px',
                padding: '4px'
              }}
            >
              ✕
            </button>
          </div>
        )}
        <form onSubmit={handleSubmit} className="message-input-wrapper">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          <button
            type="button"
            onClick={() => setShowTemplateSelector(true)}
            className="template-button"
            disabled={disabled || sending || !chatId}
            style={{
              background: 'none',
              border: 'none',
              cursor: disabled || sending || !chatId ? 'not-allowed' : 'pointer',
              fontSize: '20px',
              padding: '8px',
              color: '#16a34a',
              opacity: disabled || sending || !chatId ? 0.5 : 1
            }}
            title="Use WhatsApp template"
          >
            📱
          </button>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="attachment-button"
            disabled={disabled || sending}
            style={{
              background: 'none',
              border: 'none',
              cursor: disabled || sending ? 'not-allowed' : 'pointer',
              fontSize: '24px',
              padding: '8px',
              color: '#6b7280',
              opacity: disabled || sending ? 0.5 : 1
            }}
            title="Attach file"
          >
            📎
          </button>
          <textarea
            ref={textareaRef}
            value={message}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            placeholder="Type your message..."
            className="message-input"
            disabled={disabled || sending}
            rows={1}
          />
          <button
            type="submit"
            className="send-button"
            disabled={(!message.trim() && !selectedFile) || sending || disabled}
          >
            {uploading ? (
              <div className="loading-spinner"></div>
            ) : sending ? (
              <div className="loading-spinner"></div>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
              </svg>
            )}
          </button>
        </form>
      </div>

      {/* Template Modals */}
      {showTemplateSelector && (
        <TemplateSelector
          onClose={() => setShowTemplateSelector(false)}
          onSelect={(template) => {
            setSelectedTemplate(template);
            setShowTemplateSelector(false);
          }}
        />
      )}

      {selectedTemplate && chatId && (
        <TemplateComposer
          template={selectedTemplate}
          chatId={chatId}
          onClose={() => setSelectedTemplate(null)}
          onSent={() => {
            setSelectedTemplate(null);
            if (onTemplateSent) {
              onTemplateSent();
            }
          }}
        />
      )}
    </>
  );
};

export default MessageInput;
